import { clsx } from 'clsx';
import { Button } from '@mui/material';
import { RcFile } from 'antd/lib/upload';
import { Col, Row, Modal, Checkbox } from 'antd';
import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FilePdfFilled, SaveFilled } from '@ant-design/icons';
import { useInput, useNotify, useRedirect, useRefresh } from 'react-admin';

import { contractHttp, s3http } from '@network';
import { GuarantorsForm } from '../GuarantorsForm';
import { Contract, ContractStatus as StatusType } from '@types';
import { dateFormatter, inputParser, moneyFormatter } from '@utils';
import {
  AntdDateInput,
  ConfirmDelete,
  Container,
  FileControlled,
  FileUpload, InfoTooltip,
  InitialCard,
  InitialRow,
  Loader,
  NumberInput, TextAreaInput,
  TextInput,
} from '@components';
import { GetFileBtn } from '@pages/contracts/Components/GetFileBtn';
import { ContractStatus } from '@pages/contracts/Components/ContractStatus';
import cls from './ContractsForm.module.css';

export const ContractsForm = ({ record }: { record?: Contract }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);
  const [copy, setCopy] = useState(false);
  const [file, setFile] = useState<{ id: number; adminUrl: string; ext: string } | null>(null);
  const redirect = useRedirect();
  const refresh = useRefresh();
  const notify = useNotify();
  const { getValues } = useFormContext<Contract>();
  const values = getValues();
  const labelSize = 9;
  const { field: { onChange: changeNotarialDeedText } } = useInput({ source: 'notarialDeedText' });
  const noFillingCertText = useWatch({ name: 'noFillingCertText' });
  const editable = Boolean(record && (record.status === StatusType.PENDING
    || record.status === StatusType.COMPLETED));

  const handleUpdateContract = async (): Promise<void> => {
    setBtnLoader(true);
    try {
      await contractHttp.update(values);
      notify('Contract was updated successfully.');
      refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setBtnLoader(false);
    }
  };

  const setStatus = async (status: StatusType): Promise<void> => {
    setBtnLoader(true);
    try {
      if (!record) throw new Error('Record not found');
      await contractHttp.setStatus({ id: record.id, status });
      notify('Contract status has been updated.');
      refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setBtnLoader(false);
    }
  };

  const openModal = () => {
    setDialogIsOpen(true);
  };

  const handleDialogClose = () => {
    setDialogIsOpen(false);
    setFile(null);
  };

  const handleCancel = () => {
    redirect('/contract/');
  };

  const uploadAction = async (rcFile: RcFile): Promise<string> => {
    const name = rcFile.name.split('.');
    const ext = name.length ? name[name.length - 1] : 'jpeg';
    const { id, adminUrl } = await s3http.uploadFile(rcFile as File);
    setFile({ id, adminUrl, ext });

    return adminUrl;
  };

  const signAction = async (): Promise<void> => {
    if (!record || !file) return;
    await contractHttp.sendSign({
      fileId: file.id,
      id: record.id,
    });
    handleDialogClose();
    refresh();
  };

  useEffect(() => {
    if (record) {
      setLoading(false);
    }
  }, [record]);

  useEffect(() => {
    if (copy) {
      changeNotarialDeedText(noFillingCertText);
    }
  }, [noFillingCertText, copy]);

  const isCompany = record?.accredited === 'company';

  return !loading && record ? (
    <Container max>
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={24}>
          <InitialCard left="General Info" extraPadding>
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={12}>
                <InitialRow title="Credit" fontLg labelSize={labelSize}>
                  {record.creditCode}
                </InitialRow>
                <InitialRow title="Accredited" fontLg labelSize={labelSize}>
                  {record.accreditedName}
                </InitialRow>
                <InitialRow title="Birthdate/DOC" fontLg labelSize={labelSize}>
                  {!editable ? dateFormatter.toDateSlash(record.userBirthday) : (
                    <AntdDateInput source="userBirthday" format="DD/MM/YYYY" />
                  )}
                </InitialRow>
                <InitialRow title="No. Filing Cert" fontLg labelSize={labelSize}>
                  {!editable ? (
                    <div className={cls.trim}>{record.noFillingCert}</div>
                  ) : (
                    <TextInput source="noFillingCert" inputProps={{ maxLength: 100 }}/>
                  )}
                </InitialRow>
                <InitialRow title="Notarial Deed" fontLg labelSize={labelSize}>
                  {!editable ? (record.notarialDeed) : (
                    <TextInput
                      source="notarialDeed"
                      inputProps={{ maxLength: 15 }}
                      parse={inputParser.onlyNumbers}
                    />
                  )}
                </InitialRow>
                <div className={cls.ta_wrap}>
                  <div className={cls.ta_fields}>
                    <InitialRow title="" titleClassName={cls.textareaTitle} fullWidth>
                      <TextAreaInput
                        source="noFillingCertText"
                        placeholder="No. Filing Certificate Txt."
                        className={cls.textarea}
                        maxLength={150}
                      />
                    </InitialRow>
                    <InitialRow title="" titleClassName={cls.textareaTitle} fullWidth>
                      <TextAreaInput
                        source="notarialDeedText"
                        placeholder="Notarial Deed Txt."
                        className={cls.textarea}
                        maxLength={150}
                        disabled={copy}
                      />
                    </InitialRow>
                  </div>
                  <div className={cls.ta_check}>
                    <Checkbox
                      onChange={() => setCopy(!copy)}
                      checked={copy}
                      className={cls.ta_checkbox}
                    >Same</Checkbox>
                  </div>
                </div>
              </Col>
              <Col xs={24} lg={12}>
                <InitialRow title={`Legal Rep${isCompany ? '*' : ''}`} fontLg labelSize={labelSize}>
                  {!editable ? (record.userFullname) : (
                    <TextInput source={isCompany ? 'userFullname' : ''} inputProps={{ maxLength: 100 }} disabled={!isCompany} />
                  )}
                </InitialRow>
                <InitialRow title="RFC" fontLg labelSize={labelSize}>
                  {record.rfc}
                </InitialRow>
                <InitialRow title="Accredited Bank" fontLg labelSize={labelSize}>
                  {!editable ? (record.accreditedBank) : (
                    <TextInput source="accreditedBank" inputProps={{ maxLength: 30 }}/>
                  )}
                </InitialRow>
                <InitialRow title="Account holder" fontLg labelSize={labelSize}>
                  {!editable ? (record.accountHolder) : (
                    <TextInput source="accountHolder" inputProps={{ maxLength: 100 }}/>
                  )}
                </InitialRow>
                <InitialRow title="CLABE" fontLg labelSize={labelSize}>
                  {!editable ? (record.clabe) : (
                    <TextInput
                      source="clabe"
                      inputProps={{ maxLength: 18 }}
                      parse={inputParser.onlyNumbers}
                    />
                  )}
                </InitialRow>
                <InitialRow title="Address" fontLg labelSize={labelSize}>
                  {record.userAddress}
                </InitialRow>
                <InitialRow title="Phone" fontLg labelSize={labelSize}>
                  {record.userPhone}
                </InitialRow>
                <InitialRow title="Email" fontLg labelSize={labelSize}>
                  {record.userEmail}
                </InitialRow>
              </Col>
            </Row>
          </InitialCard>
        </Col>
        <Col xs={24} xl={12}>
          <GuarantorsForm data={record} editable={editable} />
        </Col>
        <Col xs={24} xl={12}>
          <InitialCard left="Other Data" extraPadding>
            <InitialRow title="Membership Rate" fontLg labelSize={labelSize}>
              {record.membershipRate} %
            </InitialRow>
            <InitialRow title="Disposal Fee" fontLg labelSize={labelSize}>
              {!editable ? (record.disposalFee) : (
                <NumberInput source="disposalFee" />
              )}
            </InitialRow>
            <InitialRow title="Signature of Contract" fontLg labelSize={labelSize}>
              {/*http://localhost:8000/#/credit/1744*/}
              {!editable ? (dateFormatter.toDateSlash(record.date || undefined)) : (
                <AntdDateInput source="date" format="DD/MM/YYYY" />
              )}
            </InitialRow>
            <InitialRow title="Place" fontLg labelSize={labelSize}>
              {!editable ? (record.place) : (
                <TextInput source="place" inputProps={{ maxLength: 100 }} />
              )}
            </InitialRow>
            <InitialRow title="Description of pledged assets" fontLg labelSize={labelSize}>
              {!editable ? (record.description) : (
                <TextInput source="description" inputProps={{ maxLength: 150 }} />
              )}
            </InitialRow>
            <InitialRow title="Contract Status" fontLg labelSize={labelSize}>
              <ContractStatus status={record.status} />
            </InitialRow>
          </InitialCard>
        </Col>
        <Col xs={24} sm={24}>
          <InitialCard left="Credit Info" extraPadding>
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={12}>
                <InitialRow title="CAT" fontLg labelSize={labelSize}>
                  {record.interestRateCat} %
                </InitialRow>
                <InitialRow title="Interest Rate" fontLg labelSize={labelSize}>
                  {record.interestRate} %
                </InitialRow>
                <InitialRow title="Moratory Rate" fontLg labelSize={labelSize}>
                  {record.moratoryRate} %
                </InitialRow>
                <InitialRow title="Credit Amount" fontLg labelSize={labelSize}>
                  {!editable ? (moneyFormatter.format(record.creditAmount)) : (
                    <NumberInput source="creditAmount" />
                  )}
                </InitialRow>
                <InitialRow title="Credit Amount txt" fontLg labelSize={labelSize}>
                  {!editable ? (record.creditAmountText) : (
                    <TextInput source="creditAmountText" inputProps={{ maxLength: 100 }} />
                  )}
                </InitialRow>
                <InitialRow title="Payment Amount" fontLg labelSize={labelSize}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {moneyFormatter.format(record.paymentAmount)}
                    <InfoTooltip title={
                      <pre>{JSON.stringify(record.paymentAmountCalc || {}, null, 2)}</pre>
                    } />
                  </div>
                </InitialRow>
                <InitialRow title="P. Amount txt" fontLg labelSize={labelSize}>
                  {!editable ? (record.paymentAmountText) : (
                    <TextInput source="paymentAmountText" inputProps={{ maxLength: 100 }} />
                  )}
                </InitialRow>
              </Col>
              <Col xs={24} lg={12}>
                <InitialRow title="Credit Term" fontLg labelSize={labelSize}>
                  {record.creditTerms}
                </InitialRow>
                <InitialRow title="Disbursement Date" fontLg labelSize={labelSize}>
                  {!editable ? (dateFormatter.toDateSlash(record.signatureDate || undefined)) : (
                    <AntdDateInput source="signatureDate" format="DD/MM/YYYY" />
                  )}
                </InitialRow>
                <InitialRow title="Contract Term" fontLg labelSize={labelSize}>
                  {record.contractTerms}
                </InitialRow>
                <InitialRow title="Payment Date" fontLg labelSize={labelSize}>
                  {!editable ? (dateFormatter.toDateSlash(record.paymentDate || undefined)) : (
                    <AntdDateInput source="paymentDate" format="DD/MM/YYYY" />
                  )}
                </InitialRow>
                <InitialRow title="Use of Credit" fontLg labelSize={labelSize}>
                  {!editable ? (record.useOfCredit) : (
                    <TextInput source="useOfCredit" inputProps={{ maxLength: 100 }} />
                  )}
                </InitialRow>
                <InitialRow title="Drawdowns" fontLg labelSize={labelSize}>
                  {!editable ? (record.drawdowns) : (
                    <TextInput
                      source="drawdowns"
                      inputProps={{ maxLength: 18 }}
                      parse={inputParser.onlyNumbers}
                    />
                  )}
                </InitialRow>
                <InitialRow title="Promissory Rate" fontLg labelSize={labelSize}>
                  {record.promissoryRate}
                </InitialRow>
                <InitialRow title="Promissory Rate txt" fontLg labelSize={labelSize}>
                  {!editable ? (record.promissoryRateText) : (
                    <TextInput source="promissoryRateText" inputProps={{ maxLength: 100 }} />
                  )}
                </InitialRow>
              </Col>
            </Row>
          </InitialCard>
        </Col>
      </Row>
      <Row gutter={[24, 24]}>
        <Col xs={12} md={8} className={cls.actionsLeft}>
          <ConfirmDelete title="Are you sure?" cb={handleCancel}>
            <Button
              variant="outlined"
              color="inherit"
              className={clsx(cls.action, cls.cancelBtn)}
              onClick={undefined}
            >Go back</Button>
          </ConfirmDelete>
        </Col>
        {!record.status || record.status === StatusType.PENDING
          ? <Col xs={12} md={16} className={cls.actionsRight}>
            <Button
              variant="outlined"
              color="inherit"
              className={clsx(cls.action, cls.saveBtn)}
              onClick={handleUpdateContract}
              disabled={btnLoader}
            >
              {btnLoader
                ? <Loader mini className={cls.loader} />
                : <SaveFilled className={cls.actionIcon}/>}Save and Sync
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              className={clsx(cls.action, cls.completeBtn)}
              onClick={() => setStatus(StatusType.COMPLETED)}
              disabled={btnLoader}
            >
              {btnLoader
                ? <Loader mini className={cls.loader} />
                : <FilePdfFilled className={cls.actionIcon}/>}Complete Contract
            </Button>
          </Col> : null}
        {record.status === StatusType.COMPLETED
          ? <Col xs={12} md={16} className={cls.actionsRight}>
            <Button
              variant="outlined"
              color="inherit"
              className={clsx(cls.action, cls.saveBtn)}
              onClick={handleUpdateContract}
              disabled={btnLoader}
            >
              {btnLoader
                ? <Loader mini className={cls.loader} />
                : null} Update
            </Button>
            <GetFileBtn
              url={record.draftDocument?.files[0]?.adminUrl}
              fName={`${record?.creditCode}_draftContract.pdf`}
              viewOnly={false}
              type="pdf"
              disabled={btnLoader}
            >
              <Button
                variant="outlined"
                color="inherit"
                className={clsx(cls.action, cls.downloadBtn)}
                disabled={btnLoader}
              >
                {btnLoader
                  ? <Loader mini className={cls.loader} />
                  : <FilePdfFilled className={cls.actionIcon}/>}Download
              </Button>
            </GetFileBtn>
            <Button
              variant="outlined"
              color="inherit"
              className={clsx(cls.action, cls.toSignBtn)}
              disabled={btnLoader}
              onClick={() => setStatus(StatusType.SIGNATURE_IN_PROCESS)}
            >Ready to Sign</Button>
          </Col> : null}
        {record.status === StatusType.SIGNATURE_IN_PROCESS
          ? <Col xs={12} md={16} className={cls.actionsRight}>
            <Button
              variant="outlined"
              color="inherit"
              className={clsx(cls.action, cls.cancelSignBtn)}
              onClick={() => setStatus(StatusType.COMPLETED)}
            >Cancel Signature</Button>
            <Button
              variant="outlined"
              color="inherit"
              className={clsx(cls.action, cls.signedBtn)}
              onClick={openModal}
            >Contract is Signed</Button>
          </Col> : null}
        {record.status === StatusType.SIGNED
          ? <Col xs={12} md={16} className={cls.actionsRight}>
            <GetFileBtn
              url={record.signedDocument?.files[0]?.adminUrl}
              fName={`${record?.creditCode}_draftContract.docx`}
              type="docx"
            >
              <Button
                variant="outlined"
                color="inherit"
                className={clsx(cls.action, cls.previewActionBtn)}
              >Preview Contract</Button>
            </GetFileBtn>
          </Col> : null}
      </Row>

      <Modal
        width={470}
        title={false}
        open={dialogIsOpen}
        footer={false}
        onCancel={handleDialogClose}
        centered
        closable={false}
        maskClosable={false}
        className={clsx(cls.modal, cls.scoreModal)}
      >
        <div className={cls.dialogTitle}>Upload Signed Contract, please</div>
        <div className={cls.file}>
          {file ? (
            <FileControlled
              wrapClass={clsx(cls.filePreview, file.ext === 'pdf' && cls.filePdf)}
              adminUrl={file.adminUrl}
              isPdf={file.ext === 'pdf'}
              downloadFilename={`Contract_${record.id}_signed.${file.ext}`}
              onDelete={() => setFile(null)}
            />
          ) : (
            <FileUpload
              uploadAction={uploadAction}
              className={cls.uploadBtn}
              wrapperClassName={cls.uploader}
            />
          )}
        </div>
        <div className={cls.dialogBtns}>
          <Button onClick={handleDialogClose} className={clsx(cls.action, cls.cancelBtn)} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            onClick={signAction}
            className={clsx(cls.action, cls.signedBtn, !file && cls.actionDisabled)}
            disabled={!file}
            variant="outlined"
            color="inherit"
          >
            Send and Finish
          </Button>
        </div>
      </Modal>
    </Container>
  ) : <Loader center/>;
};
